import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Row, Col, Avatar, Descriptions, Tag, Divider, message } from 'antd';
import {
  SaveOutlined,
  FileTextOutlined,
  CheckCircleOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  HomeOutlined,
  LinkOutlined,
  ClockCircleOutlined,
  CalendarOutlined,
  PictureOutlined,
} from '@ant-design/icons';
import { financeSettingsApi, show422Error, ValidationError } from '../../utils/api';

const templateOptions = [
  { value: 'invoice-1', label: 'قالب ۱', color: '#1890ff', desc: 'قالب کلاسیک' },
  { value: 'invoice-2', label: 'قالب ۲', color: '#52c41a', desc: 'قالب مدرن' },
  { value: 'invoice-3', label: 'قالب ۳', color: '#722ed1', desc: 'قالب حرفه‌ای' },
  { value: 'invoice-4', label: 'قالب ۴', color: '#fa8c16', desc: 'قالب ساده' },
  { value: 'invoice-5', label: 'قالب ۵', color: '#eb2f96', desc: 'قالب رسمی' },
];

const InvoiceTemplateTab = ({ data, onUpdate }) => {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [templateData, setTemplateData] = useState(null);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    if (data?.template) {
      setTemplateData(data.template);
      setSelectedTemplate(data.template.template);
      setCompany(data.template.company || null);
    }
  }, [data]);

  const handleSubmit = async () => {
    if (!selectedTemplate) {
      message.warning('لطفا یک قالب انتخاب کنید');
      return;
    }
    setLoading(true);
    try {
      await financeSettingsApi.updateTemplateSettings({ template: selectedTemplate });
      message.success('قالب فاکتور با موفقیت ذخیره شد');
      onUpdate?.();
    } catch (error) {
      console.error('Error updating template settings:', error);
      if (error instanceof ValidationError) {
        show422Error(error);
      } else {
        message.error('خطا در ذخیره قالب');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ fontFamily: 'CustomFont' }}>
      {/* انتخاب قالب */}
      <Card
        className="mb-4"
        size="small"
        title={
          <span style={{ fontFamily: 'CustomFont' }}>
            <FileTextOutlined className="ml-2 text-blue-500" />
            انتخاب قالب فاکتور
          </span>
        }
      >
        <Row gutter={[16, 16]}>
          {templateOptions.map((t) => {
            const isActive = selectedTemplate === t.value;
            return (
              <Col xs={24} sm={12} md={8} lg={4} xl={4} key={t.value}>
                <div
                  onClick={() => setSelectedTemplate(t.value)}
                  className="cursor-pointer transition-all"
                  style={{
                    border: isActive ? `2px solid ${t.color}` : '2px solid #e8e8e8',
                    borderRadius: 12,
                    padding: 16,
                    textAlign: 'center',
                    backgroundColor: isActive ? `${t.color}10` : '#fff',
                    boxShadow: isActive ? `0 4px 12px ${t.color}30` : 'none',
                    transform: isActive ? 'scale(1.02)' : 'scale(1)',
                  }}
                >
                  <div
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: 10,
                      backgroundColor: isActive ? t.color : '#f0f0f0',
                      color: isActive ? '#fff' : '#bfbfbf',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 8px',
                      fontSize: 20,
                      transition: 'all 0.2s',
                    }}
                  >
                    <FileTextOutlined />
                  </div>
                  <div className="font-semibold text-sm" style={{ color: isActive ? t.color : '#595959' }}>
                    {t.label}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{t.desc}</div>
                  {isActive && (
                    <Tag icon={<CheckCircleOutlined />} color="success" className="mt-2" style={{ fontSize: 11 }}>
                      فعال
                    </Tag>
                  )}
                </div>
              </Col>
            );
          })}
        </Row>

        <div className="flex justify-end mt-4">
          <Button
            type="primary"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={handleSubmit}
            className="bg-green-600 hover:bg-green-700 border-0"
            style={{ fontFamily: 'CustomFont' }}
          >
            ذخیره قالب
          </Button>
        </div>
      </Card>

      {/* لوگو و امضا */}
      {templateData && (
        <Card
          className="mb-4"
          size="small"
          title={<span style={{ fontFamily: 'CustomFont' }}><PictureOutlined className="ml-2 text-purple-500" />لوگو و امضا</span>}
        >
          <Row gutter={[24, 16]}>
            <Col xs={24} sm={12}>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-2" style={{ fontFamily: 'CustomFont' }}>لوگوی فاکتور</div>
                {templateData.logo_url ? (
                  <div className="inline-flex items-center justify-center w-28 h-20 rounded-lg border border-gray-200 bg-white p-2">
                    <img
                      src={templateData.logo_url}
                      alt="لوگوی فاکتور"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <Avatar icon={<PictureOutlined />} shape="square" size={80} style={{ backgroundColor: '#f5f5f5', color: '#bfbfbf' }} />
                )}
              </div>
            </Col>
            <Col xs={24} sm={12}>
              <div className="text-center">
                <div className="text-xs text-gray-500 mb-2" style={{ fontFamily: 'CustomFont' }}>امضای مجاز</div>
                {templateData.authorised_signatory_signature_url ? (
                  <div className="inline-flex items-center justify-center w-28 h-20 rounded-lg border border-gray-200 bg-white p-2">
                    <img
                      src={templateData.authorised_signatory_signature_url}
                      alt="امضای مجاز"
                      className="max-w-full max-h-full object-contain"
                    />
                  </div>
                ) : (
                  <div className="text-gray-400 text-sm p-4 bg-gray-50 rounded-lg inline-block" style={{ fontFamily: 'CustomFont' }}>
                    تنظیم نشده
                  </div>
                )}
              </div>
            </Col>
          </Row>
        </Card>
      )}

      {/* اطلاعات شرکت */}
      {company && (
        <Card
          size="small"
          title={<span style={{ fontFamily: 'CustomFont' }}><HomeOutlined className="ml-2 text-green-500" />اطلاعات شرکت در فاکتور</span>}
        >
          <Descriptions
            bordered
            column={{ xs: 1, sm: 2, md: 3 }}
            size="small"
            labelStyle={{ fontFamily: 'CustomFont', fontWeight: 600, backgroundColor: '#fafafa', whiteSpace: 'nowrap' }}
            contentStyle={{ fontFamily: 'CustomFont' }}
          >
            <Descriptions.Item label={<><HomeOutlined className="ml-1" />نام شرکت</>}>
              {company.company_name || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><MailOutlined className="ml-1" />ایمیل</>}>
              {company.company_email || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><PhoneOutlined className="ml-1" />تلفن</>}>
              <span dir="ltr">{company.company_phone || '-'}</span>
            </Descriptions.Item>
            <Descriptions.Item label={<><LinkOutlined className="ml-1" />وب‌سایت</>}>
              {company.website ? (
                <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-blue-500">
                  {company.website}
                </a>
              ) : '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><HomeOutlined className="ml-1" />آدرس</>}>
              {company.address || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><GlobalOutlined className="ml-1" />زبان</>}>
              {company.locale === 'fa' ? 'فارسی' : company.locale === 'en' ? 'انگلیسی' : company.locale === 'ar' ? 'عربی' : company.locale || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><ClockCircleOutlined className="ml-1" />منطقه زمانی</>}>
              {company.timezone || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={<><CalendarOutlined className="ml-1" />فرمت تاریخ</>}>
              <Tag color="blue">{company.date_format || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="فرمت زمان">
              <Tag color="blue">{company.time_format || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="وضعیت">
              {company.status === 'active' ? (
                <Tag icon={<CheckCircleOutlined />} color="success">فعال</Tag>
              ) : (
                <Tag color="default">{company.status || '-'}</Tag>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="قالب فعال">
              <Tag color="purple">{company.active_theme || '-'}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="RTL">
              {company.rtl ? <Tag color="green">فعال</Tag> : <Tag color="default">غیرفعال</Tag>}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </div>
  );
};

export default InvoiceTemplateTab;
